import { Component, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ArticleBlock } from '@modules/knowledge/models/article/article-block';
import { isVimeoPageUrl, resolveVimeoOembedSrc, videoUrlToEmbedUrl } from '@modules/knowledge/utils/video-embed';

// Shared across instances so revisiting an article reuses the same SafeResourceUrl
// and never re-resolves an already-parsed videoUrl.
const embedUrlCache = new Map<string, SafeResourceUrl>();

@Component({
    selector: 'app-article-block',
    templateUrl: './article-block.component.html',
    styleUrls: ['./article-block.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ArticleBlockComponent implements OnChanges {
  @Input() block!: ArticleBlock;

  private sanitizer = inject(DomSanitizer);
  videoEmbedUrl: SafeResourceUrl | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['block']) {
      this.resolveVideoEmbedUrl();
    }
  }

  private resolveVideoEmbedUrl(): void {
    this.videoEmbedUrl = null;

    const url = this.block?.videoUrl;
    if (this.block?.type !== 'video' || !url) {
      return;
    }

    const cached = embedUrlCache.get(url);
    if (cached) {
      this.videoEmbedUrl = cached;
      return;
    }

    const embedUrl = videoUrlToEmbedUrl(url);
    if (embedUrl) {
      this.videoEmbedUrl = this.trustAndCache(url, embedUrl);
      return;
    }

    if (isVimeoPageUrl(url)) {
      resolveVimeoOembedSrc(url).then(src => {
        if (src && this.block?.videoUrl === url) {
          this.videoEmbedUrl = this.trustAndCache(url, src);
        }
      });
    }
  }

  private trustAndCache(url: string, embedUrl: string): SafeResourceUrl {
    const safe = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    embedUrlCache.set(url, safe);
    return safe;
  }
}
